import React, { useState, useEffect } from 'react';
import {
  FaUser, FaBuilding, FaBriefcase, FaMoneyBillWave,
  FaCalculator, FaArrowLeft, FaPercentage, FaMoneyCheckAlt, FaShieldAlt
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import './PaySalary.css';

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const PaySalary = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const { salaryId, collegeName } = location.state || {};

  const [salary,      setSalary]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [liveAdvBal,  setLiveAdvBal]  = useState(0);
  const [advAdj,      setAdvAdj]      = useState(0);
  const [payMode,     setPayMode]     = useState('Bank Account');
  const [payBank,     setPayBank]     = useState('');
  const [payDate,     setPayDate]     = useState(new Date().toISOString().split('T')[0]);
  const [refNumber,   setRefNumber]   = useState('');
  const [paying,      setPaying]      = useState(false);
  const [banks,       setBanks]       = useState([]);

  useEffect(() => {
    if (!salaryId) { navigate(`${basePath}/salary`); return; }
    
    // Load banks
    api.get('/banks', { params: { limit: 1000 } })
      .then(r => setBanks(r.data.data || []))
      .catch(() => setBanks([]));
    
    api.get(`/salary/${salaryId}`)
      .then(async r => {
        const s = r.data.data;
        setSalary(s);
        setAdvAdj(s.advanceAdjust || 0);
        // fetch live advance balance
        const empId = s.employee?._id || s.employee;
        try {
          const er = await api.get('/employees', { params: { limit: 500 } });
          const found = (er.data.data || []).find(e => String(e._id) === String(empId));
          setLiveAdvBal(found?.advanceBalance ?? 0);
        } catch { setLiveAdvBal(0); }
      })
      .catch(() => navigate(`${basePath}/salary`))
      .finally(() => setLoading(false));
  }, [salaryId, basePath, navigate]);

  if (loading) return <div className="pay-salary-loader">Loading...</div>;
  if (!salary)  return null;

  const base      = salary.monthlySalary     || 0;
  const pfAmt     = salary.pfDeduction       || 0;
  const esiAmt    = salary.esiDeduction      || 0;
  const earned    = salary.earnedSalary      || 0;
  const otherDed  = salary.otherDeduction    || 0;
  const secDed    = salary.securityDeduction || 0;
  const secRet    = salary.securityReturn    || 0;
  const presentDays = salary.presentDays     || 0;
  const absentDays  = salary.absentDays      || 0;

  const pfPct     = base > 0 ? +((pfAmt  / base) * 100).toFixed(2) : 0;
  const esiPct    = base > 0 ? +((esiAmt / base) * 100).toFixed(2) : 0;
  const dailyRate = base / 30;

  const adj    = parseFloat(advAdj) || 0;
  // Recalculate net pay live with updated advance adjustment
  const netPay = earned - otherDed - adj - secDed + secRet;

  const handleAdvChange = (val) => {
    const n = parseFloat(val) || 0;
    if (n > liveAdvBal)  setAdvAdj(liveAdvBal);
    else if (n < 0)      setAdvAdj(0);
    else                 setAdvAdj(val);
  };

  const monthLabel = MONTHS[(salary.month || 1) - 1];

  const handlePay = async (e) => {
    e.preventDefault();
    if (payMode !== 'Cash' && !payBank) {
      alert('Please select a debited bank.');
      return;
    }
    if (!payDate) {
      alert('Please select payment date.');
      return;
    }
    if (payMode !== 'Cash' && (!refNumber || refNumber.trim() === '')) {
      alert('Please enter reference number.');
      return;
    }
    setPaying(true);
    try {
      // Re-save draft with updated advance adjustment & net salary
      await api.post('/salary', {
        employee:          salary.employee?._id || salary.employee,
        college:           salary.college?._id  || salary.college,
        month:             salary.month,
        year:              salary.year,
        monthlySalary:     base,
        pfDeduction:       pfAmt,
        esiDeduction:      esiAmt,
        otherDeduction:    otherDed,
        advanceAdjust:     adj,
        presentDays,
        absentDays,
        daysInMonth:       salary.daysInMonth || 30,
        earnedSalary:      earned,
        totalDeductions:   pfAmt + esiAmt + otherDed + adj + secDed,
        netSalary:         netPay,
        securityDeduction: secDed,
        securityReturn:    secRet,
      });
      // Mark as paid
      const { data } = await api.patch(`/salary/${salaryId}/pay`, {
        paymentMode: payMode,
        bank:        payMode !== 'Cash' ? payBank : undefined,
        paymentDate: payDate,
        paymentRef:  payMode !== 'Cash' ? refNumber : undefined,
      });
      navigate(`${basePath}/salary/receipt`, { state: { salary: data.data, collegeName }, replace: true });
    } catch (err) {
      alert(err.response?.data?.message || 'Payment failed.');
    } finally { setPaying(false); }
  };

  return (
    <div className="pay-salary-container">
      <div className="pay-salary-header">
        <button className="pay-salary-back-btn" type="button" onClick={() => navigate(`${basePath}/salary`)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="pay-salary-title">Pay Salary — {monthLabel} {salary.year}</h2>
      </div>

      <form className="pay-salary-form" onSubmit={handlePay}>

        {/* Employee Details */}
        <div className="pay-salary-section">
          <h3 className="pay-salary-section-title"><FaUser /> Employee Details</h3>
          <div className="pay-salary-grid pay-salary-grid-3">
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">College</label>
              <div className="pay-salary-input-wrapper">
                <FaBuilding className="pay-salary-icon" />
                <input type="text" className="pay-salary-input pay-salary-readonly"
                  value={collegeName || salary.college?.name || '—'} readOnly disabled />
              </div>
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Employee Name</label>
              <div className="pay-salary-input-wrapper">
                <FaUser className="pay-salary-icon" />
                <input type="text" className="pay-salary-input pay-salary-readonly"
                  value={salary.employee?.name || '—'} readOnly disabled />
              </div>
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Department</label>
              <div className="pay-salary-input-wrapper">
                <FaBriefcase className="pay-salary-icon" />
                <input type="text" className="pay-salary-input pay-salary-readonly"
                  value={salary.employee?.department?.name || '—'} readOnly disabled />
              </div>
            </div>
          </div>
        </div>

        {/* Salary Structure */}
        <div className="pay-salary-section">
          <h3 className="pay-salary-section-title"><FaMoneyBillWave /> Salary Structure</h3>
          <div className="pay-salary-grid pay-salary-grid-4">
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Monthly Salary (₹)</label>
              <input type="text" className="pay-salary-input pay-salary-readonly"
                value={`₹ ${base.toFixed(2)}`} readOnly disabled />
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">PF (%)</label>
              <div className="pay-salary-input-wrapper">
                <FaPercentage className="pay-salary-icon" />
                <input type="text" className="pay-salary-input pay-salary-readonly"
                  value={`${pfPct}%`} readOnly disabled />
              </div>
              <span className="pay-salary-hint-text">₹ {pfAmt.toFixed(2)}</span>
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">ESI (%)</label>
              <div className="pay-salary-input-wrapper">
                <FaPercentage className="pay-salary-icon" />
                <input type="text" className="pay-salary-input pay-salary-readonly"
                  value={`${esiPct}%`} readOnly disabled />
              </div>
              <span className="pay-salary-hint-text">₹ {esiAmt.toFixed(2)}</span>
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Daily Rate (₹)</label>
              <input type="text" className="pay-salary-input pay-salary-highlight-input"
                value={`₹ ${dailyRate.toFixed(2)}`} readOnly disabled />
              <span className="pay-salary-hint-text">Monthly ÷ 30 days (fixed)</span>
            </div>
          </div>
        </div>

        {/* Attendance & Adjustments */}
        <div className="pay-salary-section">
          <h3 className="pay-salary-section-title"><FaCalculator /> Attendance & Adjustments</h3>
          <div className="pay-salary-grid pay-salary-grid-4">
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Earned Salary (₹)</label>
              <input type="text" className="pay-salary-input pay-salary-highlight-input"
                value={`₹ ${earned.toFixed(2)}`} readOnly disabled />
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Other Deduction (₹)</label>
              <input type="text" className="pay-salary-input pay-salary-readonly"
                value={`₹ ${otherDed.toFixed(2)}`} readOnly disabled />
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Advance Balance (₹)</label>
              <input type="text" className="pay-salary-input pay-salary-readonly"
                value={`₹ ${liveAdvBal.toLocaleString()}`} readOnly disabled />
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Advance Adjustment (₹)</label>
              <input type="number" className="pay-salary-input" value={advAdj}
                onChange={e => handleAdvChange(e.target.value)} min="0" max={liveAdvBal} />
              {liveAdvBal > 0 && <span className="pay-salary-hint-text">Max: ₹ {liveAdvBal.toLocaleString()}</span>}
            </div>
          </div>
        </div>

        {/* Security */}
        {(secDed > 0 || secRet > 0) && (
          <div className="pay-salary-section">
            <h3 className="pay-salary-section-title"><FaShieldAlt /> Security Money</h3>
            <div className="pay-salary-grid pay-salary-grid-4">
              {secDed > 0 && (
                <div className="pay-salary-input-group">
                  <label className="pay-salary-label">Security Deduction (₹)</label>
                  <input type="text" className="pay-salary-input pay-salary-deduct-input"
                    value={`- ₹ ${secDed.toFixed(2)}`} readOnly disabled />
                </div>
              )}
              {secRet > 0 && (
                <div className="pay-salary-input-group">
                  <label className="pay-salary-label">Security Return (Employee Left)</label>
                  <input type="text" className="pay-salary-input pay-salary-return-input"
                    value={`+ ₹ ${secRet.toFixed(2)}`} readOnly disabled />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Mode */}
        <div className="pay-salary-section">
          <h3 className="pay-salary-section-title"><FaMoneyCheckAlt /> Payment Details</h3>
          <div className="pay-salary-grid pay-salary-grid-4">
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Payment Date <span className="pay-salary-required">*</span></label>
              <input
                type="date"
                className="pay-salary-input"
                value={payDate}
                onChange={e => setPayDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                required
                style={!payDate ? {borderColor: '#ef4444'} : {}}
              />
            </div>
            <div className="pay-salary-input-group">
              <label className="pay-salary-label">Payment Mode</label>
              <select className="pay-salary-input" value={payMode}
                onChange={e => { setPayMode(e.target.value); setPayBank(''); setRefNumber(''); }} required>
                <option value="Bank Account">Bank Account</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Net Banking">Net Banking</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            {payMode !== 'Cash' && (
              <>
                <div className="pay-salary-input-group">
                  <label className="pay-salary-label">Reference Number <span className="pay-salary-required">*</span></label>
                  <input
                    type="text"
                    className="pay-salary-input"
                    value={refNumber}
                    onChange={e => setRefNumber(e.target.value)}
                    placeholder="Enter reference/transaction number"
                    required
                    style={!refNumber ? {borderColor: '#ef4444'} : {}}
                  />
                </div>
                <div className="pay-salary-input-group">
                  <label className="pay-salary-label">Debited Bank <span className="pay-salary-required">*</span></label>
                  <select 
                    className="pay-salary-input" 
                    value={payBank}
                    onChange={e => setPayBank(e.target.value)}
                    required
                    style={!payBank ? {borderColor: '#ef4444'} : {}}
                  >
                    <option value="">-- Select Bank --</option>
                    {banks
                      .filter(b => !salary?.college?._id || b.colleges?.some(c => String(c._id || c) === String(salary.college._id || salary.college)))
                      .map(b => (
                        <option key={b._id} value={b._id}>
                          {b.name} - {b.accountNumber}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="pay-salary-final-container">
          <div className="pay-salary-receipt-box">
            <h4 className="pay-salary-receipt-title">Payment Summary</h4>
            <div className="pay-salary-receipt-row">
              <span className="pay-salary-receipt-label">Earned Salary</span>
              <span className="pay-salary-receipt-val">₹ {earned.toFixed(2)}</span>
            </div>
            {otherDed > 0 && (
              <div className="pay-salary-receipt-row pay-salary-receipt-deduct">
                <span className="pay-salary-receipt-label">Less: Other Deductions</span>
                <span className="pay-salary-receipt-val">- ₹ {otherDed.toFixed(2)}</span>
              </div>
            )}
            {adj > 0 && (
              <div className="pay-salary-receipt-row pay-salary-receipt-deduct">
                <span className="pay-salary-receipt-label">Less: Advance Adjustment</span>
                <span className="pay-salary-receipt-val">- ₹ {adj.toFixed(2)}</span>
              </div>
            )}
            {secDed > 0 && (
              <div className="pay-salary-receipt-row pay-salary-receipt-deduct">
                <span className="pay-salary-receipt-label">Less: Security Deduction</span>
                <span className="pay-salary-receipt-val">- ₹ {secDed.toFixed(2)}</span>
              </div>
            )}
            {secRet > 0 && (
              <div className="pay-salary-receipt-row pay-salary-receipt-add">
                <span className="pay-salary-receipt-label">Add: Security Return (Employee Left)</span>
                <span className="pay-salary-receipt-val">+ ₹ {secRet.toFixed(2)}</span>
              </div>
            )}
            <div className="pay-salary-receipt-divider" />
            <div className="pay-salary-receipt-row pay-salary-receipt-total">
              <span className="pay-salary-receipt-label">Net Payable</span>
              <span className="pay-salary-receipt-val">₹ {netPay.toFixed(2)}</span>
            </div>
          </div>
          <div className="pay-salary-action-box">
            <button type="submit" className="pay-salary-save-btn" disabled={paying}>
              <FaMoneyCheckAlt /> {paying ? 'Processing...' : 'Confirm & Pay'}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default PaySalary;
